import { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { CustomPanelType, CustomPanelTypeShape, ICustomZShapeType } from "../../../../responses/customPanelTypes";
import { ComponentsLibraryDialog } from "../../../custom-components-library/componentsLibraryDialog";
import { ProcessingPane } from "../../../processing-pane/processingPane";
import { rootElement, noop } from "../../../shared";
import { ImperialAndMetricDimensionEditor } from "../../editor-inputs/imperialAndMetricDimensionEditor";
import { NumberPropertyEditor } from "../../editor-inputs/numberPropertyEditor";
import { ObjectUserUniqueId } from "../../editor-inputs/objectUserUniqueId";
import { PanelTypeSelector } from "../../editor-inputs/panelTypeSelector";
import { TextPropertyEditor } from "../../editor-inputs/textPropertyEditor";
import { getCustomZShapedPanelUniqueIdObjectPrefix } from "../../ids/userObjectIds";
import { InstallationSequence } from "../../panels/panelParameters";
import { PanelStickyNote } from "./panelStickyNote";
import { Parameters } from "./parameters";
import { CustomZShapedPanelSelectionChangedEventPayload } from "../../eventBus/selectionChangedEventsPayloads";
import eventBus, { IApplicationEvent } from "../../eventBus/eventDispatcher";

type CustomZShapedPanelFields = {
    id: string;
    userUniqueId: number;
    offset: number;
    height: number;
    leftWing: number;
    shelf: number;
    rightWing: number;
    elementName: string;
    installationSequence: InstallationSequence;
    customPanelTypeId: string;
    customPanelTypeName: string;
    customPanelTypes: ICustomZShapeType[];
};

type AsyncOperation = {
    title: string;
    message: string;
};

export const CustomZShapedPanelDockingPanelContent = () => {
    const [panel, setPanel] = useState<CustomZShapedPanelFields>({
        id: "",
        userUniqueId: 0,
        offset: 0,
        height: 0,
        leftWing: 0,
        shelf: 0,
        rightWing: 0,
        elementName: "",
        installationSequence: null,
        customPanelTypeId: "",
        customPanelTypeName: "",
        customPanelTypes: [],
    });
    const [customComponentLibraryOpened, setCustomComponentLibraryOpened] = useState(false);
    const [asyncOperation, setAsyncOperation] = useState<AsyncOperation>();

    useEffect(() => {
        const onSelectionChanged = (
            event: IApplicationEvent<CustomZShapedPanelSelectionChangedEventPayload | null>,
        ) => {
            setCustomComponentLibraryOpened(false);
            setAsyncOperation(undefined);

            if (!event.payload) {
                return;
            }

            const { panel, customPanelTypes } = event.payload;
            const {
                id,
                userUniqueId,
                offset,
                height,
                leftWing,
                shelf,
                rightWing,
                elementName,
                installationSequence,
                customPanelTypeId,
            } = panel;
            const customPanelTypeName = customPanelTypes.find(x => x.id === customPanelTypeId)!.name;

            setPanel({
                id,
                userUniqueId,
                offset,
                height,
                leftWing,
                shelf,
                rightWing,
                elementName,
                installationSequence,
                customPanelTypeId,
                customPanelTypeName,
                customPanelTypes,
            });
        };

        eventBus.addEventListener("Dextall.CustomZShapedPanels.SelectionChanged", onSelectionChanged);

        return () => {
            eventBus.removeEventListener("Dextall.CustomZShapedPanels.SelectionChanged", onSelectionChanged);
        };
    }, []);

    const onSelectCustomPanelType = (customPanelType: CustomPanelType) => {
        if (customPanelType.shapeType !== CustomPanelTypeShape.ZShape) {
            throw new Error("Invalid state!");
        }

        setAsyncOperation({
            title: "Custom z-panel type",
            message: "Setting custom z-panel type...",
        });

        setCustomComponentLibraryOpened(false);

        eventBus.dispatchEvent({
            type: "Dextall.CustomZShapedPanels.UI.SwitchType",
            payload: {
                panelId: panel.id,
                targetTypeId: customPanelType.id,
            },
        });
    };

    const onElementNameChanged = (newName: string) => {
        setAsyncOperation({
            title: "Element name",
            message: "Setting element name...",
        });

        eventBus.dispatchEvent({
            type: "Dextall.CustomZShapedPanels.UI.ParametersChanged",
            payload: {
                id: panel.id,
                elementName: newName,
                installationSequence: panel.installationSequence,
            },
        });
    };

    const updatePanelInstallationSequence = (installationSequence: InstallationSequence) => {
        eventBus.dispatchEvent({
            type: "Dextall.CustomZShapedPanels.UI.ParametersChanged",
            payload: {
                id: panel.id,
                elementName: panel.elementName,
                installationSequence,
            },
        });

        setAsyncOperation({
            title: "Installation sequence",
            message: "Setting installation sequence...",
        });
    };

    const onOffsetChanged = (newOffset: number) => {
        setAsyncOperation({
            title: "Element offset",
            message: "Setting element offset...",
        });

        eventBus.dispatchEvent({
            type: "Dextall.CustomZShapedPanels.UI.SetOffset",
            payload: {
                id: panel.id,
                offset: newOffset,
            },
        });
    };

    return (
        <div className="fullheight" style={{ display: "flex", flexDirection: "column" }}>
            <Parameters>
                <ObjectUserUniqueId prefix={getCustomZShapedPanelUniqueIdObjectPrefix()} idValue={panel.userUniqueId} />
                <PanelTypeSelector
                    panelTypes={[]}
                    selectedPanelType={{ name: "fake", withoutCladdings: false }}
                    title="Panel type:"
                    selectCustomPanelTypeAvailable={true}
                    onChange={noop}
                    onEditTypeRequested={noop}
                    selectedCustomPanelTypeName={panel.customPanelTypeName}
                    onCustomPanelTypeRequested={() => setCustomComponentLibraryOpened(true)}
                />
                <TextPropertyEditor title="Panel ID:" value={panel.elementName} onChange={onElementNameChanged} />
                <NumberPropertyEditor
                    title="Installation sequence:"
                    value={panel.installationSequence}
                    onSave={updatePanelInstallationSequence}
                    onlyNatural
                />
                <ImperialAndMetricDimensionEditor title="Offset:" value={panel.offset} onChange={onOffsetChanged} />
                <ImperialAndMetricDimensionEditor
                    title="Height:"
                    value={panel.height}
                    onChange={noop}
                    disabled={true}
                />
                <ImperialAndMetricDimensionEditor
                    title="Left:"
                    value={panel.leftWing}
                    onChange={noop}
                    disabled={true}
                />
                <ImperialAndMetricDimensionEditor title="Shelf:" value={panel.shelf} onChange={noop} disabled={true} />
                <ImperialAndMetricDimensionEditor
                    title="Right:"
                    value={panel.rightWing}
                    onChange={noop}
                    disabled={true}
                />
                <PanelStickyNote id={panel.id} type="custom-z-panels" />

                {customComponentLibraryOpened &&
                    ReactDOM.createPortal(
                        <ComponentsLibraryDialog
                            closeDialog={() => setCustomComponentLibraryOpened(false)}
                            customPanelTypes={panel.customPanelTypes}
                            selectLibraryComponent={onSelectCustomPanelType}
                            currentCustomPanelTypeId={panel.customPanelTypeId}
                        />,
                        rootElement,
                    )}
                {asyncOperation &&
                    ReactDOM.createPortal(
                        <ProcessingPane
                            isVisible={true}
                            message={asyncOperation.message}
                            title={asyncOperation.title}
                        />,
                        rootElement,
                    )}
            </Parameters>
        </div>
    );
};
